import { readFile, stat } from "fs/promises";
import { join } from "path";

/**
 * The only accepted project icon files, checked at the project root.
 * `.favicon.svg` is the hidden variant for projects that don't want the
 * icon visible in the file tree. No other names, no directory fallbacks.
 */
export const ICON_FILENAMES = ["favicon.svg", ".favicon.svg"];

export interface ProjectIcon {
  data: string;
  mimeType: string;
}

const MAX_ICON_SIZE = 32 * 1024; // 32KB max

/**
 * Find a project icon at the root of the given directory.
 * Only `favicon.svg` and `.favicon.svg` qualify — no fallback search.
 *
 * @param projectDir - The root directory of the project
 * @returns The absolute path to the found icon, or null if not found
 */
export async function findProjectIcon(projectDir: string): Promise<string | null> {
  for (const filename of ICON_FILENAMES) {
    const fullPath = join(projectDir, filename);
    try {
      const stats = await stat(fullPath);
      if (stats.isFile()) {
        return fullPath;
      }
    } catch {
      // File doesn't exist, try the next candidate
    }
  }
  return null;
}

/**
 * Find and read the project icon, returning it as base64.
 * Only returns icons smaller than MAX_ICON_SIZE (32KB).
 *
 * @param projectDir - The root directory of the project
 * @returns The icon data with mime type, or null if not found
 */
export async function getProjectIcon(projectDir: string): Promise<ProjectIcon | null> {
  const iconPath = await findProjectIcon(projectDir);
  if (!iconPath) {
    return null;
  }

  try {
    const stats = await stat(iconPath);
    if (stats.size > MAX_ICON_SIZE) {
      return null;
    }

    const buffer = await readFile(iconPath);
    const data = buffer.toString("base64");
    return { data, mimeType: "image/svg+xml" };
  } catch {
    return null;
  }
}
